/**
 * Main entry point for client-side JavaScript application
 *
 * This file is the primary entry point for the frontend JavaScript bundle.
 * It handles:
 * - Global event listener optimizations for performance
 * - SVG sprite loading and hiding
 * - Component initialization
 * - Cookie consent management
 * - Hot Module Replacement (HMR) for development
 *
 * @module client/index
 * @see {@link https://webpack.js.org/concepts/entry-points/} Webpack Entry Points
 */

import ComponentHelper from './components/00_utilities/helpers/ComponentHelper';
import LinkFocusHelper from './components/00_utilities/helpers/LinkFocusHelper';
import ScrollToHelper from './components/00_utilities/helpers/ScrollToHelper';
import CookieHelper from './components/00_utilities/helpers/CookieHelper';
import YoutubeHelper from './components/00_utilities/helpers/YoutubeHelper';
import NoscriptImageHelper from './components/00_utilities/helpers/NoscriptImageHelper';

const componentHelper = new ComponentHelper();

/* eslint-disable no-underscore-dangle, camelcase */
/**
 * SVG Sprite Loading and Hiding
 *
 * The SVG sprite is loaded asynchronously via XHR and inserted into the DOM.
 * We use a MutationObserver to detect when the sprite is added and hide it
 * using the HTML hidden attribute.
 *
 * @see {@link https://www.npmjs.com/package/webpack-svgstore-plugin} webpack-svgstore-plugin
 */
// Webpack-specific variables and requires that are defined/injected at build time
// Set __webpack_public_path__ BEFORE loading SVG sprite in development mode.
// Origin comes from DefinePlugin (__devServerOrigin__): DDEV HTTPS :8085 or host localhost.
if (__devMode__) {
    __webpack_public_path__ = `${__devServerOrigin__}/assets/`;
    // svgxhr prepends baseUrl so the sprite loads from the webpack-dev-server origin
    if (typeof window !== 'undefined') {
        window.baseUrl = __devServerOrigin__;
    }
}

// Set SVG filename (relative path - svgxhr helper will prepend baseUrl)
const __svg__ = {
    path: './svg/*.svg',
    name: 'Icons/icons.svg',
    filename: 'assets/Icons/icons.svg',
};
if (typeof document !== 'undefined') {
    const observer = new MutationObserver(() => {
        // The svgxhr helper inserts the div as the first child of body
        const svgContainer = document.body?.firstElementChild;

        // Guard clauses: early return if conditions not met
        if (!svgContainer) return;
        if (svgContainer.tagName !== 'DIV') return;
        if (!svgContainer.querySelector('svg')) return;

        // All conditions met: hide the container
        svgContainer.hidden = true;
        observer.disconnect();
    });

    if (document.body) {
        observer.observe(document.body, { childList: true, subtree: false });
    } else {
        document.addEventListener('DOMContentLoaded', () => {
            observer.observe(document.body, { childList: true, subtree: false });
        });
    }
}

require('webpack-svgstore-plugin/src/helpers/svgxhr')(__svg__);
/* eslint-enable */

/**
 * Unwrap cached lazysizes noscript placeholders before components measure layout.
 * Does not wait for `window.load` (images would stay empty until then).
 */
const bootResponsiveImages = () => {
    NoscriptImageHelper.unwrapAll();
    NoscriptImageHelper.enableLazyImagesForPrint();
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootResponsiveImages);
} else {
    bootResponsiveImages();
}

/**
 * Returning visitors with maps consent: start the Map chunk before `window.load`
 * (images etc. would delay Google API otherwise). ConsentGateBoot already set
 * `html[data-consent-maps]`. Other components still wait for `load`.
 */
const bootConsentedMapsEarly = () => {
    if (!document.documentElement.hasAttribute('data-consent-maps')) {
        return;
    }
    document.querySelectorAll('[data-component="03_molecules/Map"]').forEach((node) => {
        componentHelper.initializeDataComponent(node);
    });
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootConsentedMapsEarly);
} else {
    bootConsentedMapsEarly();
}

/**
 * Brand-transition modal: boot on DOMContentLoaded (same as consented maps),
 * not `window.load`. The dialog stays closed until the cookie-banner (CMP)
 * has settled; we still need the listeners attached before Usercentrics
 * fires `UC_UI_INITIALIZED` / `CMP_SHOWN`, which can happen while images
 * are loading. Markup exists only when the TypoScript flag is on.
 */
const bootBrandTransitionDialog = () => {
    document.querySelectorAll('[data-component="03_molecules/BrandTransitionDialog"]').forEach((node) => {
        componentHelper.initializeDataComponent(node);
    });
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootBrandTransitionDialog);
} else {
    bootBrandTransitionDialog();
}

/**
 * Page Initialization
 *
 * Initializes all components and helpers when the page has fully loaded.
 * This ensures all DOM elements are available before component initialization.
 */
window.addEventListener('load', () => {
    window.youtubePlayers = [];

    // Initialize all data-component elements on the page
    componentHelper.initializePage();

    // Handle hash links (e.g., #section) with proper focus management
    LinkFocusHelper.handleHashLink();

    // Initialize smooth scrolling for anchor links
    ScrollToHelper.initializeAnchorLinks();

    // Initialize marketing cookie consent for third-party services
    const customEventsOnMarketingAccept = ['consent-type-youtube', 'consent-type-google-maps'];
    CookieHelper.addMarketingCookieListener(customEventsOnMarketingAccept);
    YoutubeHelper.addYoutubeConsentListener();
});

/**
 * Check for hot module replacement and use it if possible
 */
if (module.hot) {
    module.hot.accept();
}
